/*@flow*/
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import React, { type ElementConfig } from 'react';
import { useState, useEffect } from 'react';
import Select, { components } from 'react-select';
import filterByLabel from 'lib/filterByLabel';
import same from 'lib/same';
import Blank from 'lib/Blank';
import * as I from 'immutable';
import useCachedQuery from 'lib/useCachedQuery';
import { GetNearbyResources } from './queries/GetNearbyResources';
import { GetResourceList } from '../queries/GetResourceList';

export type Option = { value: any, label: string };

const customStyles = {
  input: (base: Object) => ({ ...base, border: 'none' }),
  menu: (base: Object) => ({ ...base, maxWidth: 400 }),
  valueContainer: (base: Object) => ({ ...base, paddingLeft: 0 }),
  control: (base: Object) => ({
    ...base,
    border: 'none',
    borderBottom: 'solid 1px #ccc',
    borderRadius: 0,
    maxWidth: 400,
    boxShadow: 'none',
  }),
};

export type PickerProps = {
  theGeom: string,
  id?: ?(ID | ID[]),
  selectProps?: { [key: string]: any },
  onChange: (Option | Option[]) => any,
};

export function mkComponent(props: {
  placeholder: string,
  targetRelation: string,
  isMulti?: boolean,
}) {
  return (nextProps: PickerProps) => {
    if (!nextProps.theGeom) {
      return (
        <div
          data-testid="DisabledGeoResourcePicker"
          css={css('cursor: not-allowed; position: relative;')}
        >
          <input
            type="text"
            disabled
            placeholder={props.placeholder}
            className="form-control input-sm p-3 rounded border"
            css={css('font-size: 1.6rem;')}
          />
          <i
            css={css(
              `
              border: solid 1px white;
              position: absolute;
              right: 40px;
              bottom: 0px;
              top: 0px;
            `
            )}
          />
        </div>
      );
    }

    return (
      <Resources
        isMulti={props.isMulti}
        placeholder={props.placeholder}
        targetRelation={props.targetRelation}
        id={nextProps.id}
        theGeom={nextProps.theGeom}
        selectProps={nextProps.selectProps}
        onChange={nextProps.onChange}
      />
    );
  };
}

const DropdownIndicator = (
  props: ElementConfig<typeof components.DropdownIndicator>
) => {
  return (
    <components.DropdownIndicator {...props}>
      <i className="fal fa-map-marker-smile" css={css('color: #666;')} />
    </components.DropdownIndicator>
  );
};

const pressedEffect = css(`
  &:active  {
    top: 3px !important;
    position: relative;
  }
`);

function SearchRadiusComponent({
  value,
  onChange,
}: {
  value: number,
  onChange: (number) => any,
}) {
  const [localValue, setValue] = useState(value);
  useEffect(() => {
    if (localValue !== value) onChange(localValue);
  }, [localValue]);

  return (
    <div
      data-testid="SearchRadiusComponent"
      className="small d-flex justify-content-between align-items-center"
    >
      <label htmlFor="searchRadius" className="mb-0">
        Search radius: ( {localValue} meters)
      </label>
      <div className="d-flex">
        <button
          data-testid="LessButton"
          className="btn btn-link p-0 mt-0"
          onClick={() => setValue((v) => Math.max(v - 50, 0))}
          css={pressedEffect}
        >
          <span className="fa-stack text-dark small">
            <i className="fas fa-square fa-stack-2x" />
            <i className="fa fa-minus fa-stack-1x fa-inverse" />
          </span>
        </button>
        <button
          data-testid="MoreButton"
          className="btn btn-link p-0 mt-0"
          onClick={() => setValue((v) => Math.min(v + 50, 1010))}
          css={pressedEffect}
        >
          <span className="fa-stack text-dark small">
            <i className="fas fa-square fa-stack-2x" />
            <i className="fa fa-plus fa-stack-1x fa-inverse" />
          </span>
        </button>
      </div>
    </div>
  );
}

function getNewOptsFilter(data, key = 'nearbyResources') {
  const newOpts = I.getIn(data, ['me', 'company', key], []).map((v) => ({
    value: v.id,
    label: v.name,
  }));
  const ids = newOpts.map((v) => String(v.value));
  const filter = (opts) => {
    return opts.filter((v2) => !ids.includes(String(v2.value))).concat(newOpts);
  };
  return { filter };
}

const menuHeaderStyle = {
  padding: '8px 12px',
  background: '#white',
  color: '#333',
  borderBottom: 'solid 1px #fefe',
};

export function Resources(props: {
  isMulti?: boolean,
  targetRelation: string,
  placeholder?: string,
  id?: ?(ID | ID[]),
  theGeom?: string,
  onChange: (Option | Option[]) => any,
  selectProps?: { [key: string]: any },
}) {
  const {
    isMulti,
    placeholder,
    theGeom,
    id,
    targetRelation,
    onChange,
    selectProps,
  } = props;

  const [options, setOptions] = useState([]);
  const [searchRadiusInMeters, setSearchRadius] = useState(10);

  const { data, loading, error } = useCachedQuery(GetNearbyResources, {
    variables: {
      theGeom,
      targetRelation,
      searchRadiusInMeters,
    },
  });

  const idSet = useCachedQuery(GetResourceList, {
    variables: {
      id: Array.isArray(id) ? id : [id],
      resourceName: targetRelation,
    },
    skip: Blank.is(id) || Blank.is(id),
  });

  useEffect(() => {
    if (loading) return;
    const { filter } = getNewOptsFilter(data);
    setOptions((v) => filter(v));
  }, [data, loading, searchRadiusInMeters]);

  useEffect(() => {
    if (idSet.loading) return;
    const { filter } = getNewOptsFilter(idSet.data, 'resources');
    setOptions((v) => filter(v));
  }, [idSet.data, idSet.loading]);

  const MenuList = (menuListProps) => {
    return (
      <components.MenuList {...menuListProps}>
        <div style={menuHeaderStyle}>
          <SearchRadiusComponent
            value={searchRadiusInMeters}
            onChange={(v) => setSearchRadius(v)}
          />
        </div>
        {menuListProps.children}
      </components.MenuList>
    );
  };

  const finder = (opt) => {
    if (Array.isArray(id)) {
      return id.map(String).includes(String(opt.value));
    }
    return String(opt.value) === String(id);
  };
  const targetOptionOrOptions = isMulti
    ? options.filter(finder)
    : options.find(finder);

  return (
    <React.Fragment>
      {idSet.error && (
        <div className="text-danger">{idSet.error.toString()}</div>
      )}
      {error && <div className="text-danger">{error.toString()}</div>}
      <Select
        {...selectProps || {}}
        placeholder={placeholder || 'Select...'}
        isMulti={isMulti}
        options={options}
        isClearable
        cacheOptions
        components={{ DropdownIndicator, MenuList }}
        filterOption={filterByLabel}
        onChange={onChange}
        isLoading={loading}
        value={targetOptionOrOptions}
        styles={customStyles}
      />
    </React.Fragment>
  );
}
